import { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { SkeletonStyleProps } from 'react-loading-skeleton'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router-dom'
import {
  DEFAULT_SKELETON_COLOR,
  DEFAULT_SKELETON_HIGHLIGHT_COLOR
} from 'shared/constants'
import { AppConfig, Stand } from './types'

export const STAND = import.meta.env.VITE_STAND as Stand

export const BACKEND_HOST = import.meta.env.VITE_BACKEND_HOST
const useHttps = import.meta.env.VITE_DEV_HTTPS !== 'false'
const hasProtocol = /^https?:\/\//.test(BACKEND_HOST)
const baseUrl =
  hasProtocol ? BACKEND_HOST : (
    `${useHttps ? 'https' : 'http'}://${BACKEND_HOST}`
  )
/** Origin for REST calls (e.g. /api/user-metadata); respects VITE_DEV_HTTPS. */
export const BACKEND_BASE_URL = baseUrl.replace(/\/$/, '')
const wsBaseUrl =
  hasProtocol ?
    BACKEND_HOST.replace(/^https/, 'wss').replace(/^http/, 'ws')
  : `${useHttps ? 'wss' : 'ws'}://${BACKEND_HOST}`
export const BACKEND_GRAPHQL_URL = `${BACKEND_BASE_URL}/graphql`
export const SUBSCRIPTION_URL = `${wsBaseUrl.replace(/\/$/, '')}/subscription`

export const IS_TECHNICAL_ISSUES_STUB_ENABLED =
  import.meta.env.VITE_TECH_ISSUES_FLAG == 'true'

export const MAINNET_FLAGS: AppConfig = {
  auth: {
    isDiscordAllowed: true,
    isSignUpByEmailAllowed: true,
    isWalletAllowed: true,
    isAuthRequired: true,
    isReferralCodeFlowAllowed: false
  },
  tokenMintAllowed: false,
  isMainNetFeature: true,
  bullsEyeLeaderboard: false,
  offchainOnly: false,
  mobile: {
    upDown: false,
    oneVsOne: false,
    bullsEye: false,
    setups: false,
    rewards: false
  }
}

export const PROD_FLAGS: AppConfig = {
  auth: {
    isDiscordAllowed: true,
    isSignUpByEmailAllowed: true,
    isWalletAllowed: true,
    isAuthRequired: true,
    isReferralCodeFlowAllowed: true
  },
  tokenMintAllowed: true,
  isMainNetFeature: false,
  bullsEyeLeaderboard: false,
  offchainOnly: false,
  mobile: {
    upDown: false,
    oneVsOne: false,
    bullsEye: false,
    setups: false,
    rewards: false
  }
}

export const DEV_FLAGS: AppConfig = {
  auth: {
    isDiscordAllowed: true,
    isSignUpByEmailAllowed: true,
    isWalletAllowed: true,
    isAuthRequired: true,
    isReferralCodeFlowAllowed: true
  },
  tokenMintAllowed: true,
  isMainNetFeature: true,
  bullsEyeLeaderboard: true,
  offchainOnly: true,
  mobile: {
    upDown: false,
    oneVsOne: false,
    bullsEye: false,
    setups: false,
    rewards: false
  }
}

export const SENTRY_CONFIG = {
  // NOTE: public DSN; also feeds the CSP report-uri in security-headers.mjs
  dsn: import.meta.env.VITE_SENTRY_DSN ?? '',
  environment: STAND || Stand.prod || Stand.mainnet,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    })
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: [/^https:\/\/xyro\.io/],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0
}

export const THEME_CONFIG = {
  accentColor: 'sky' as const,
  radius: 'full' as const,
  hasBackground: true, // NOTE: to inject Radix CSS variables into a <body/>
  grayColor: 'gray' as const,
  appearance: 'dark' as const,
  panelBackground: 'solid' as const,
  scaling: '100%' as const
}

export const SKELETON_CONFIG: SkeletonStyleProps = {
  baseColor: DEFAULT_SKELETON_COLOR,
  highlightColor: DEFAULT_SKELETON_HIGHLIGHT_COLOR,
  borderRadius: '2rem'
}

export const UNAUTHORIZED = 'Unauthorized'
export const POLLING_INTERVAL = 30 * 1000 //ms
